import { Component, OnInit, ViewChild } from '@angular/core';
import { SpreadsheetComponent, SpreadsheetHeader, SpreadsheetSnapshot } from '@app/core/components/spreadsheet/spreadsheet.component';
import { ModalService } from '@app/core/services/modal.service';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataImportGet, DataImportService } from '@app/services/data-import.service';
import { P2ImportProcListItem, P2ImportProcService } from '@app/services/p2-import-proc.service';
import { P2ExportProcListItem, P2ExportProcService } from '@app/services/p2-export-proc.service';
import { TypeaheadService } from '@app/services/typeahead.service';

@Component({ selector: 'tt-excel-import', templateUrl: './excel-import.component.html', styleUrls: ['./excel-import.component.css'], standalone: false })
export class ExcelImportComponent implements OnInit {
    public page: DataImportGet = { p2_importproc_keyno: '0', p2_exportproc_keyno: '0', encodingtype: '' };
    public source = 'FILE';
    public loading = true;
    public spreadsheet: SpreadsheetSnapshot = { filename: '', encoding: '', records: [] };
    public headers: SpreadsheetHeader[] = [];
    public importProcedures: P2ImportProcListItem[] = [];
    public exportProcedures: P2ExportProcListItem[] = [];
    public importing = false;
    public exporting = false;
    public reading = false;
    private importConfiguring = false;
    private exportConfiguring = false;
    public get configuring(): boolean { return this.importConfiguring || this.exportConfiguring; }
    public get hasImportProcedure(): boolean { return Number(this.page.p2_importproc_keyno) > 0; }
    public get hasExportProcedure(): boolean { return Number(this.page.p2_exportproc_keyno) > 0; }
    public get busy(): boolean { return this.importing || this.exporting || this.reading; }
    public error = '';
    @ViewChild(SpreadsheetComponent) public workbook?: SpreadsheetComponent;
    private importRevision = 0;
    private exportRevision = 0;

    constructor(private readonly imports: DataImportService, private readonly importProcs: P2ImportProcService,
        private readonly exportProcs: P2ExportProcService, private readonly typeahead: TypeaheadService, private readonly modal: ModalService) {}

    public get verifyDisabled(): boolean { return !this.spreadsheet.records.length || !Number(this.page.p2_importproc_keyno) || this.importing || this.configuring || this.reading; }
    public async ngOnInit(): Promise<void> {
        this.loading = true;
        try { [this.page, this.importProcedures] = await Promise.all([this.imports.getSettings(), this.importProcs.getImportProceduresList({})]); }
        catch (error) { this.showError(error); }
        finally { this.loading = false; }
    }
    public async selectSource(source: string): Promise<void> {
        if (source !== this.source) {
            ++this.importRevision;
            ++this.exportRevision;
            this.importConfiguring = false;
            this.exportConfiguring = false;
        }
        this.source = source;
        if (source === 'DB' && !this.exportProcedures.length) {
            try { this.exportProcedures = await this.exportProcs.getExportProcedures({ exclude_inactive: 1, exclude_grid_export: 1 }); }
            catch (error) { this.showError(error); }
        }
    }
    public async onImportProcedureChanged(value: string | number): Promise<void> {
        if (value === this.page.p2_importproc_keyno) return;
        this.page.p2_importproc_keyno = value;
        const revision = ++this.importRevision;
        if (!value || !Number(value)) { this.importConfiguring = false; return; }
        this.importConfiguring = true;
        try {
            const procedure = await this.importProcs.getImportProcedure({ p2_importproc_keyno: value });
            if (revision === this.importRevision) this.page.show_table_name_destination = procedure.show_table_name_destination;
        } catch (error) { this.showError(error); }
        finally { if (revision === this.importRevision) this.importConfiguring = false; }
    }
    public async onExportProcedureChanged(value: string | number): Promise<void> {
        if (value === this.page.p2_exportproc_keyno) return;
        this.page.p2_exportproc_keyno = value;
        const revision = ++this.exportRevision;
        if (!Number(value)) { this.exportConfiguring = false; return; }
        this.exportConfiguring = true;
        try {
            const procedure = await this.exportProcs.getExportProc({ p2_exportproc_keyno: value });
            if (revision !== this.exportRevision) return;
            this.page.p2_importproc_keyno = procedure.p2_importproc_keyno;
            this.page.p2_datatask_keyno = procedure.p2_datatask_keyno;
            this.page.show_table_name_export = procedure.show_table_name_export;
        } catch (error) { this.showError(error); }
        finally { if (revision === this.exportRevision) this.exportConfiguring = false; }
    }
    public async onSpreadsheetChanged(spreadsheet: SpreadsheetSnapshot): Promise<void> {
        const oldFilename = this.spreadsheet.filename;
        this.spreadsheet = spreadsheet;
        if (spreadsheet.filename !== oldFilename) { ++this.importRevision; this.importConfiguring = false; }
        if (this.source !== 'FILE' || !spreadsheet.filename || spreadsheet.filename === oldFilename || !spreadsheet.records.length) return;
        const revision = ++this.importRevision;
        this.importConfiguring = true;
        try {
            const detected = await this.importProcs.detectFileProcedure({ filename: spreadsheet.filename,
                filecontent: spreadsheet.filename.endsWith('.xlsx') ? '' : this.imports.encodeFileRecords(spreadsheet.records), origin: 'FILE', getmethod: 'MANUAL' });
            if (revision !== this.importRevision) return;
            this.page.p2_importproc_keyno = detected.p2_importproc_keyno;
            this.page.encodingtype = detected.encodingtype;
        } catch (error) { this.showError(error); }
        finally { if (revision === this.importRevision) this.importConfiguring = false; }
    }
    public searchTables = (params?: { value?: string }): Promise<Item[]> => this.typeahead.lookUpTables({ table_name: params?.value ?? '' });
    public selectTable(field: 'table_name_destination' | 'table_name_export', item?: Item): void { this.page[field] = item?.item_name ?? ''; }
    public async importData(verify: 0 | 1): Promise<void> {
        if (this.importing || this.configuring || this.reading || !this.hasImportProcedure || !this.spreadsheet.filename || !this.spreadsheet.records.length) return;
        this.importing = true; this.error = '';
        try {
            const result = await this.imports.importRecords(this.page, this.spreadsheet, this.headers, verify);
            const failed = result.exec_resultcode === '-1';
            await this.modal.show({ type: failed ? 'danger' : 'primary', title: failed ? 'Advarsel' : 'Informasjon', message: result.exec_message,
                buttons: [{ label: 'OK', cssClass: 'btn-warning', close: true }] });
        } catch (error) { this.showError(error); }
        finally { this.importing = false; }
    }
    public async exportGet(): Promise<void> {
        if (this.exporting || this.configuring || !Number(this.page.p2_exportproc_keyno) || !Number(this.page.p2_datatask_keyno)) return;
        this.exporting = true; this.error = '';
        try { this.workbook?.loadRecords(await this.imports.exportRecords(this.page)); }
        catch (error) { this.showError(error); }
        finally { this.exporting = false; }
    }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
